import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import TopolPlugin from '@topol.io/editor';
import { Button, Text } from '@Sage/hoi-poi-ui';

import { getTopolOptions, LOAD_TIMEOUT, MAX_RETRIES, RETRY_DELAY } from './topolConfig';
import Loading from 'components/Loading';
import { getLiteral } from 'utils/getLiteral';
import './styles.scss';

const EmailCampaigns = memo(() => {
    const containerRef = useRef(null);
    const topolInitialized = useRef(false);
    const loadTimeoutRef = useRef(null);
    const retryTimeoutRef = useRef(null);

    const [editorState, setEditorState] = useState({
        status: 'initializing',
        errorMessage: null,
        retryCount: 0,
    });

    const clearTimers = useCallback(() => {
        if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
            loadTimeoutRef.current = null;
        }

        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, []);

    const safeDestroyTopol = useCallback(() => {
        if (!topolInitialized.current) return;
        if (!TopolPlugin || typeof TopolPlugin.destroy !== 'function') return;

        try {
            TopolPlugin.destroy();
        } catch (error) {
            console.error('Error destroying Topol:', error);
        }
    }, []);

    const initializeTopol = useCallback((retryAttempt = 0) => {
        if (!containerRef.current) {
            setEditorState({
                status: 'error',
                errorMessage: 'Container not available',
                retryCount: retryAttempt,
            });
            return;
        }

        if (!TopolPlugin || typeof TopolPlugin.init !== 'function') {
            if (retryAttempt < MAX_RETRIES) {
                setEditorState({
                    status: 'initializing',
                    errorMessage: null,
                    retryCount: retryAttempt + 1,
                });

                retryTimeoutRef.current = setTimeout(
                    () => initializeTopol(retryAttempt + 1),
                    RETRY_DELAY,
                );
                return;
            }

            setEditorState({
                status: 'error',
                errorMessage: 'Topol plugin not available after retries',
                retryCount: retryAttempt,
            });
            return;
        }

        const options = getTopolOptions({
            onSave: (json, html) => {
                console.log('Topol save - JSON:', json);
                console.log('Topol save - HTML:', html);
            },
            onInit: () => {
                if (loadTimeoutRef.current) {
                    clearTimeout(loadTimeoutRef.current);
                    loadTimeoutRef.current = null;
                }

                setEditorState({
                    status: 'ready',
                    errorMessage: null,
                    retryCount: 0,
                });
            },
            onError: (error) => {
                console.error('Topol initialization error:', error);
                setEditorState({
                    status: 'error',
                    errorMessage: error?.message || 'Unknown error initializing editor',
                    retryCount: 0,
                });
            },
        });

        try {
            TopolPlugin.init(options);
            topolInitialized.current = true;

            loadTimeoutRef.current = setTimeout(() => {
                setEditorState((prev) => {
                    if (prev.status !== 'ready') {
                        return {
                            ...prev,
                            status: 'error',
                            errorMessage: 'Editor loading timeout - please refresh the page',
                        };
                    }

                    return prev;
                });
            }, LOAD_TIMEOUT);
        } catch (error) {
            console.error('Error initializing Topol:', error);
            setEditorState({
                status: 'error',
                errorMessage: error?.message || 'Failed to initialize editor',
                retryCount: 0,
            });
        }
    }, []);

    useEffect(() => {
        if (topolInitialized.current) return;

        initializeTopol();

        return () => {
            clearTimers();
            safeDestroyTopol();
            topolInitialized.current = false;
        };
    }, [clearTimers, initializeTopol, safeDestroyTopol]);

    useEffect(() => {
        if (editorState.status !== 'ready') return;

        fetch('https://tlapi.github.io/topol-editor/templates/1.json')
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.text();
            })
            .then((template) => {
                if (TopolPlugin && typeof TopolPlugin.load === 'function') {
                    TopolPlugin.load(template);
                }
            })
            .catch((error) => {
                console.error('Error loading template:', error);
            });
    }, [editorState.status]);

    const handleRetry = useCallback(() => {
        clearTimers();
        safeDestroyTopol();
        topolInitialized.current = false;
        setEditorState({
            status: 'initializing',
            errorMessage: null,
            retryCount: 0,
        });
        initializeTopol();
    }, [clearTimers, initializeTopol, safeDestroyTopol]);

    return (
        <div className="email-campaigns">
            {editorState.status === 'initializing' && (
                <div className="email-campaigns__loading">
                    <Loading />
                </div>
            )}

            {editorState.status === 'error' && (
                <div className="email-campaigns__error">
                    <Text type="body" color="danger">
                        {getLiteral('error_unexpected')}
                    </Text>
                    <Button onClick={handleRetry} color="primary">
                        {getLiteral('label_retry')}
                    </Button>
                </div>
            )}

            <div
                id="topol-app"
                ref={containerRef}
                className={`email-campaigns__editor ${
                    editorState.status === 'ready' ? 'email-campaigns__editor--ready' : ''
                }`}
            />
        </div>
    );
});

export default EmailCampaigns;
